import { dropRight } from 'es-toolkit';
import React, { ChangeEventHandler, MouseEventHandler } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import RepeatIcon from '@mui/icons-material/Repeat';
import { Box, IconButton, List, ListItem, ListItemText, Tooltip } from '@mui/material';

import {
  getNameFromMitekDesignFile,
  preloadFileProcessingResult,
  processFiles,
  UploadedFile,
} from '@paragon/component-processor';
import { SealingServiceComponentDesign } from '@paragon/contract-library';
import { enqueueWarning, NotifierConfirm } from '@paragon/notification-tools';

import { formatComponentForUpload, replaceComponentDesigns } from '../../actions/job';
import { jobIsLocked } from '../../modules/jobLocking';
import { getDocuments } from '../../selectors/documents';
import { RootState } from '../../store';
import { SanitizedDocument } from '../../store/documents';

async function processComponentDesignInput(files: FileList, replace: boolean) {
  const addNameToComponent = (componentFiles: readonly UploadedFile[], allFiles: readonly UploadedFile[]) => {
    const pdfFile = componentFiles.find((file) => file.filetype!.toLowerCase() === 'pdf');
    let name;
    if (pdfFile) {
      name = getNameFromMitekDesignFile(pdfFile, allFiles).toUpperCase();
    }

    return { componentFiles, name };
  };

  const processedFiles = await processFiles(files, [], replace);
  await preloadFileProcessingResult(processedFiles);

  const allFiles = Object.values(processedFiles.componentDesigns).flatMap((files) => files);
  return Object.values(processedFiles.componentDesigns).map((componentFiles) =>
    addNameToComponent(componentFiles, allFiles),
  );
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  readonly replaceComponentsOpen: boolean;
  readonly componentsToReplace: readonly string[];
  readonly existingComponents: readonly SanitizedDocument[];
  readonly uploadedComponents: readonly SealingServiceComponentDesign[];
}

class ReplaceComponents extends React.Component<Props, State> {
  state: State = {
    replaceComponentsOpen: false,
    componentsToReplace: [],
    existingComponents: [],
    uploadedComponents: [],
  };

  getComponentName = (pdfFile: SanitizedDocument) => dropRight(pdfFile.name.split('.'), 1).join('');

  onMultiReplace: ChangeEventHandler<HTMLInputElement> = async (event) => {
    // On selection of file(s), read data
    if (event.target.files!.length === 0) {
      return;
    }

    try {
      const componentDesignsInfo = await processComponentDesignInput(event.target.files!, true);
      const existingComponentDocuments = this.props.documents.filter((document) => document.componentDesignId != null);

      const existingDesignNames = existingComponentDocuments
        .map(this.getComponentName)
        .map((name) => name.toLowerCase());

      const componentsToUpload = componentDesignsInfo
        .filter((component) => existingDesignNames.includes(component.name!.toLowerCase()))
        .map((component) => {
          const correspondingOldDocument = existingComponentDocuments.find(
            (design) => this.getComponentName(design).toLowerCase() === component.name!.toLowerCase(),
          )!;

          return {
            ...formatComponentForUpload(
              component,
              this.props.job.jobId,
              correspondingOldDocument.index,
              correspondingOldDocument.notes,
            ),
            id: correspondingOldDocument.componentDesignId,
            name: component.name!,
          };
        });

      const namesOfComponentsToBeReplaced = componentsToUpload.map((component) => component.name);

      if (componentsToUpload.length === 0) {
        this.props.enqueueWarning('No matching component designs found to replace.');
      } else {
        this.setState({
          replaceComponentsOpen: true,
          componentsToReplace: namesOfComponentsToBeReplaced,
          existingComponents: existingComponentDocuments,
          uploadedComponents: componentsToUpload,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  confirmReplaceComponents = () => {
    this.props.replaceComponentDesigns(this.state.uploadedComponents, this.state.existingComponents);
    this.closeReplaceComponents();
  };

  closeReplaceComponents = () => {
    this.setState({
      replaceComponentsOpen: false,
      componentsToReplace: [],
      existingComponents: [],
      uploadedComponents: [],
    });
  };

  onSelectFiles: MouseEventHandler<HTMLInputElement> = (event) => {
    // TODO Is this correct?
    // Clear out any files selected by a previous use of this file input
    (event.target as any).value = null;

    if (jobIsLocked()) {
      event.preventDefault();
    }
  };

  render() {
    const componentListNames = this.state.componentsToReplace.map((component) => (
      <ListItem sx={{ py: 0 }} key={component}>
        <ListItemText
          sx={{
            py: 0,
            '& span:first-child': {
              color: this.props.useDarkTheme ? 'black' : 'white',
            },
          }}
          primary={component}
        />
      </ListItem>
    ));
    const componentList = (
      <List sx={{ maxHeight: 600, overflowX: 'auto', overflowY: 'auto' }} dense>
        {componentListNames}
      </List>
    );

    return (
      <div>
        <Box
          component="input"
          sx={{ display: 'none' }}
          id="replaceComponentsInput"
          onChange={this.onMultiReplace}
          onClick={this.onSelectFiles}
          multiple
          type="file"
        />
        <Tooltip disableInteractive disableFocusListener id="replace-component-tooltip" title="Replace Components">
          <IconButton aria-label="Replace Components" sx={{ borderRadius: 0, width: '100%' }}>
            <Box component="label" htmlFor="replaceComponentsInput" sx={{ display: 'flex' }}>
              <RepeatIcon />
            </Box>
          </IconButton>
        </Tooltip>
        <NotifierConfirm
          open={this.state.replaceComponentsOpen}
          onClose={this.closeReplaceComponents}
          onClick={this.confirmReplaceComponents}
          message="Replace these component designs?"
          messageSecondary={componentList}
          buttonText="Replace"
        />
      </div>
    );
  }
}

function mapStateToProps(state: RootState) {
  return {
    documents: getDocuments(state),
    job: state.job,
    useDarkTheme: state.theme.useDarkTheme,
  };
}

const actionCreators = {
  replaceComponentDesigns,
  enqueueWarning,
  getDocuments,
};

export default connect(mapStateToProps, actionCreators)(ReplaceComponents);
